import React, { useState, useEffect, useRef } from 'react';
import { createConsumer } from '@rails/actioncable';
import ConversationHeader from './components/ConversationHeader';
import IntentsSidebar from './components/IntentsSidebar';
import MessagesContainer from './components/MessagesContainer';
import CartSidebar from './components/CartSidebar';

const ConversationApp = ({ conversationId }) => {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [intents, setIntents] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isCartSidebarOpen, setIsCartSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cableRef = useRef(null);
  const subscriptionRef = useRef(null);

  // Fetch initial conversation data
  useEffect(() => {
    fetchConversationData();
  }, [conversationId]);

  // Set up ActionCable subscription
  useEffect(() => {
    if (conversation && ['pending', 'running'].includes(conversation.status)) {
      // Only setup if not already subscribed
      if (!subscriptionRef.current) {
        setupActionCable();
      }
    } else if (conversation && ['passed', 'failed', 'errored'].includes(conversation.status)) {
      // Only unsubscribe when conversation is truly finished
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    }
    
    return () => {
      // Cleanup on component unmount
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [conversation?.status, conversationId]);

  const fetchConversationData = async () => {
    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content;
      
      const response = await fetch(`/admin/conversations/${conversationId}.json`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        credentials: 'same-origin'
      });
      
      if (response.status === 302 || response.redirected) {
        window.location.href = response.url || '/admin/sessions/new';
        return;
      }
      
      if (!response.ok) {
        const text = await response.text();
        console.error('Response:', response.status, text);
        throw new Error(`Failed to fetch conversation: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Initial data from server:', {
        conversation: data.conversation,
        messageCount: data.messages?.length,
        intents: data.intents
      });
      setConversation(data.conversation);
      setMessages(data.messages || []);
      setIntents(data.intents || []);
      setLoading(false);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const setupActionCable = () => {
    console.log('Setting up ActionCable subscription for conversation:', conversationId);
    
    if (!cableRef.current) {
      cableRef.current = createConsumer();
    }

    subscriptionRef.current = cableRef.current.subscriptions.create(
      { channel: 'ConversationChannel', id: conversationId },
      {
        connected() {
          console.log('ActionCable connected for conversation:', conversationId);
        },
        disconnected() {
          console.log('ActionCable disconnected for conversation:', conversationId);
        },
        received(data) {
          console.log('ActionCable received data:', data);
          handleCableUpdate(data);
        }
      }
    );
  };

  const handleCableUpdate = (data) => {
    console.log('Handling cable update:', data.type);
    
    if (data.type === 'conversation_update') {
      // Update state directly with the JSON data
      if (data.conversation) {
        setConversation(data.conversation);
      }
      
      if (data.messages) {
        setMessages(data.messages);
      }
      
      if (data.intents) {
        console.log('Updating intents:', data.intents);
        setIntents(data.intents);
      }
    }
  };

  const handleMessageClick = (message) => {
    setSelectedMessage(message);
    setIsCartSidebarOpen(true);
  };

  const handleCartSidebarClose = () => {
    setIsCartSidebarOpen(false);
    setSelectedMessage(null);
  };

  const handleIntentClick = (intentId) => {
    // Find the first message associated with this intent
    const firstMessage = messages.find(msg => msg.intent_id === intentId);
    if (firstMessage) {
      // Set the selected message to highlight it
      setSelectedMessage(firstMessage);
      // We'll need to pass a ref to MessagesContainer to scroll to specific message
      // For now, just highlight the message
    }
  };

  const handleRerun = async () => {
    try {
      const response = await fetch(`/admin/conversations/${conversationId}/rerun`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = `/admin/conversations/${data.conversation_id}`;
      }
    } catch (err) {
      console.error('Failed to rerun conversation:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading conversation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
      <ConversationHeader 
        conversation={conversation} 
        onRerun={handleRerun}
      />
      
      <div className="flex flex-1 overflow-hidden">
        <IntentsSidebar 
          intents={intents} 
          messages={messages}
          scenarioName={conversation.scenario_name}
          conversationStatus={conversation.status}
          errorMessage={conversation.error_message}
          onIntentClick={handleIntentClick}
        />
        
        <MessagesContainer 
          messages={messages}
          intents={intents}
          onMessageClick={handleMessageClick}
          conversationStatus={conversation.status}
          selectedMessageId={selectedMessage?.id}
        />
        
        <div className={`transition-all duration-300 ${isCartSidebarOpen ? 'w-96' : 'w-0'}`}>
          <CartSidebar 
            isOpen={isCartSidebarOpen}
            onClose={handleCartSidebarClose}
            message={selectedMessage}
            messages={messages}
          />
        </div>
      </div>
    </div>
  );
};

export default ConversationApp;